import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useMutation } from "@tanstack/react-query";
import { apiV2 } from "libs/api/src/lib/apiV2";
import { AnimatePresence, motion } from "motion/react";
import { useContext, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { Badge, Button, EyeIcon, RadioButton } from "../../../atoms";
import { XIcon } from "../../../atoms/icons/XIcon";
import { AppSettingsContext } from "../components/AppSettingsContext";
import { AppSettingsClassPreview } from "./AppSettingsClassPreview";

export const AppSettingsClasses = () => {
  const { t } = useTranslation();
  const { classesSettings, setClassesSettings } =
    useContext(AppSettingsContext);

  const [parent] = useAutoAnimate();
  const [showPreview, setShowPreview] = useState(false);
  const { mutateAsync: updateAppClassesSettings } = useMutation(
    apiV2.staff.appSettings.mutations.updateClassesSettings(),
  );
  return (
    <>
      {createPortal(
        <AnimatePresence>
          {showPreview && (
            <motion.div
              className="absolute inset-0 z-50 flex flex-col items-center justify-start bg-gray-100 pt-4 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="z-10 flex w-full flex-row items-center justify-between gap-4 p-4 pb-0">
                <div className="text-xl font-semibold text-gray-950">
                  {t("pages.settings.appSettings.navigation.preview")}
                </div>
                <Button
                  intent="transparent"
                  onClick={() => {
                    setShowPreview(false);
                  }}
                >
                  <XIcon className="stroke-gray-950" />
                </Button>
              </div>
              <AppSettingsClassPreview />
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
      <div className="flex h-full flex-col gap-6 pt-2" ref={parent}>
        <div className="flex flex-col gap-1">
          <div className="text-lg font-semibold text-gray-950">
            {t("page.settings.appSettings.classes.title")}
          </div>
          <div className="text-sm font-medium text-gray-500">
            {t("page.settings.appSettings.classes.description")}
          </div>
        </div>
        {classesSettings && (
          <div className="flex flex-col gap-2">
            <Button
              className="h-auto w-full border-gray-200 !p-3"
              onClick={() => {
                setClassesSettings({
                  classesCapacityShowMode: "DYNAMIC",
                });
              }}
            >
              <div className="flex w-full flex-row items-start justify-between gap-2">
                <div className="flex flex-row gap-2">
                  <RadioButton
                    value="DYNAMIC"
                    onChange={() => {}}
                    checked={
                      classesSettings.classesCapacityShowMode === "DYNAMIC"
                    }
                  />
                  <div className="flex flex-col items-start">
                    <div className="text-sm font-medium text-gray-950">
                      {t("page.settings.appSettings.classes.dynamic.title")}
                    </div>
                    <div className="text-left text-xs font-medium normal-case text-gray-500">
                      {t(
                        "page.settings.appSettings.classes.dynamic.description",
                      )}
                    </div>
                  </div>
                </div>
                <Badge intent="secondary" className="rounded-full">
                  {t("page.settings.appSettings.classes.dynamic.badge")}
                </Badge>
              </div>
            </Button>
            <Button
              className="h-auto w-full border-gray-200 !p-3"
              onClick={() => {
                setClassesSettings({
                  classesCapacityShowMode: "NEVER_SHOW",
                });
              }}
            >
              <div className="flex w-full flex-row items-center justify-between gap-2">
                <div className="flex flex-row gap-2">
                  <RadioButton
                    value="NEVER_SHOW"
                    onChange={() => {}}
                    checked={
                      classesSettings.classesCapacityShowMode === "NEVER_SHOW"
                    }
                  />
                  <div className="flex flex-col items-start">
                    <div className="text-sm font-medium text-gray-950">
                      {t("page.settings.appSettings.classes.neverShow.title")}
                    </div>
                  </div>
                </div>
              </div>
            </Button>
            <Button
              className="h-auto w-full border-gray-200 !p-3"
              onClick={() => {
                setClassesSettings({
                  classesCapacityShowMode: "ALWAYS_SHOW",
                });
              }}
            >
              <div className="flex w-full flex-row items-center justify-between gap-2">
                <div className="flex flex-row gap-2">
                  <RadioButton
                    value="ALWAYS_SHOW"
                    onChange={() => {}}
                    checked={
                      classesSettings.classesCapacityShowMode === "ALWAYS_SHOW"
                    }
                  />
                  <div className="flex flex-col items-start">
                    <div className="text-sm font-medium text-gray-950">
                      {t("page.settings.appSettings.classes.alwaysShow.title")}
                    </div>
                  </div>
                </div>
              </div>
            </Button>
          </div>
        )}
      </div>
      <div className="-mx-6 -mb-6 flex flex-row justify-end gap-2 border-t border-gray-300 px-6 py-4">
        <Button
          className="w-1/2 gap-1 lg:hidden"
          onClick={async () => {
            setShowPreview(true);
          }}
        >
          <EyeIcon className="stroke-gray-950" />
          {t("pages.settings.appSettings.navigation.preview")}
        </Button>
        <Button
          intent="secondary"
          className="max-lg:w-1/2 lg:px-12"
          onClick={async () => {
            if (classesSettings) {
              await updateAppClassesSettings(classesSettings);
            }
          }}
        >
          {t("portal.common.save")}
        </Button>
      </div>
    </>
  );
};
